import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { 
  GraduationCap, PiggyBank, Cpu, CheckCircle, Users, BookOpen, 
  CheckSquare, Briefcase, Trash2, ShieldCheck, ExternalLink, Rocket, Activity, Lock, LineChart
} from 'lucide-react';

import StepCard from './components/StepCard'; 

import { useAppContext } from '../../core/App';

const DashboardTelemetryCard = ({ vciScore, levelName, onClick }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };
  const handleMouseLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div 
      className="relative w-full h-64 sm:h-80 cursor-pointer perspective-1000 group"
      onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
    >
      <div className="absolute inset-0 bg-slate-950 rounded-2xl shadow-xl overflow-hidden border border-slate-700">
         <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
      </div>

      <motion.div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
         <Activity className="text-emerald-400 w-12 h-12 mb-4" />
         <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">VCI Score</h3>
         <p className="text-5xl font-black text-white tracking-tighter">{vciScore || 0}</p>
      </motion.div>

      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10 rounded-2xl">
         <div className="bg-slate-800 border border-slate-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 backdrop-blur-md transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
             <ShieldCheck size={16} /> View Compliance Profile
         </div>
      </div>

      <motion.div className="absolute bottom-4 left-4 right-4 text-center" style={{ transform: "translateZ(60px)" }}>
        <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 backdrop-blur-md text-emerald-400 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider border border-emerald-500/20 shadow-lg group-hover:opacity-0 transition-opacity">
            <Lock size={12} /> {levelName || "Tier 1: Baseline"}
        </span>
      </motion.div>
    </motion.div>
  );
};

const MissionControlWidget = ({ activeMissions, savedJobs, onCompleteMission, onRemoveJob }) => {
  const [activeTab, setActiveTab] = useState('missions');

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex border-b border-gray-100">
        <button 
          onClick={() => setActiveTab('missions')}
          className={`flex-1 py-4 text-sm font-bold text-center transition-colors flex items-center justify-center gap-2 ${activeTab === 'missions' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
        >
          <CheckSquare size={16} /> Active Ops {activeMissions?.length > 0 && <span className="ml-1 bg-blue-200 text-blue-800 text-[10px] px-1.5 py-0.5 rounded-full">{activeMissions.length}</span>}
        </button>
        <button 
          onClick={() => setActiveTab('jobs')}
          className={`flex-1 py-4 text-sm font-bold text-center transition-colors flex items-center justify-center gap-2 ${activeTab === 'jobs' ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' : 'text-gray-500 hover:bg-gray-50'}`}
        >
          <Briefcase size={16} /> Career Locker {savedJobs?.length > 0 && <span className="ml-1 bg-purple-200 text-purple-800 text-[10px] px-1.5 py-0.5 rounded-full">{savedJobs.length}</span>}
        </button>
      </div>

      <div className="p-4 min-h-[150px] max-h-[300px] overflow-y-auto custom-scrollbar">
        {activeTab === 'missions' && (
          <div className="space-y-2">
            {(!activeMissions || activeMissions.length === 0) ? (
              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                <CheckSquare size={24} className="mb-2 opacity-50" />
                <p className="text-xs font-medium">No active ops. Run the Resume Grader to find mission leads.</p>
              </div>
            ) : (
              activeMissions.map((mission, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:border-blue-300 transition-colors group shadow-sm">
                  <button 
                    onClick={() => onCompleteMission(mission)}
                    className="mt-0.5 w-5 h-5 rounded-md border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-500 hover:text-white flex items-center justify-center text-transparent transition-all"
                  >
                    <CheckCircle size={14} />
                  </button>
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 font-medium">{mission.text}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-1">Source: {mission.type}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="space-y-2">
            {(!savedJobs || savedJobs.length === 0) ? (
              <div className="flex flex-col items-center justify-center h-24 text-gray-400">
                <Briefcase size={24} className="mb-2 opacity-50" />
                <p className="text-xs font-medium">Locker Empty. Save jobs from the Resume Grader.</p>
              </div>
            ) : (
              savedJobs.map((job, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-1 min-w-0 mr-3">
                    <h4 className="font-bold text-gray-800 text-sm truncate">{job.title}</h4>
                    <a 
                      href={`https://www.google.com/search?q=${encodeURIComponent(job.title + ' jobs')}`} 
                      target="_blank" 
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                    >
                      Search Listings <ExternalLink size={10} />
                    </a>
                  </div>
                  <button 
                    onClick={(e) => {
                        e.stopPropagation(); 
                        onRemoveJob(job);
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Dashboard({ 
  userProfile, 
  step1Completed, 
  step2Completed, 
  step3Completed, 
  budgetUnlocked, 
  onNavigate, 
  onReview, 
  onStartFresh, 
  toastMessage, 
  setToastMessage,
  irsScore 
}) {
  const { completeMission, toggleSavedJob, hasAccess } = useAppContext();
  const isAdmin = userProfile?.role === 'enterprise_auditor' || userProfile?.role === 'recruiter' || userProfile?.role === 'super_admin';

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage, setToastMessage]);

  return (
    <div className="max-w-5xl mx-auto pb-12 animate-fade-in space-y-8">
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        <div className="md:col-span-5 lg:col-span-4 sticky top-6">
           <DashboardTelemetryCard vciScore={userProfile.vciScore} levelName={userProfile.readinessTier} onClick={() => onNavigate('my-career-path')} />
           
           <div className="grid grid-cols-1 mt-4">
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                 <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Verified Competency Index</p>
                 <p className="text-xl font-black text-emerald-600">{userProfile.vciScore || 0}</p>
              </div>
           </div>
           
           {isAdmin && (
             <button 
               onClick={() => onNavigate('employer-portal')}
               className="w-full mt-4 py-3 border-2 border-dashed border-slate-200 rounded-xl text-xs font-black text-slate-500 uppercase tracking-widest hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center"
             >
               <LineChart className="w-4 h-4 mr-2" /> Enterprise Dashboards
             </button>
           )}
        </div>

        <div className="md:col-span-7 lg:col-span-8 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h1 className="text-2xl font-black text-gray-900 mb-1">Telemetry Node Active. Welcome, Verified Candidate.</h1>
                <p className="text-gray-500 text-sm mb-4">
                  Institutional Readiness: <strong>{irsScore}%</strong>. Reflexes are tracking above enterprise average.
                </p>
                <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                   <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-1000 ease-out" style={{ width: `${irsScore}%` }} />
                </div>
            </div>

            <MissionControlWidget 
              activeMissions={userProfile.activeMissions} 
              savedJobs={userProfile.savedJobs}
              onCompleteMission={completeMission}
              onRemoveJob={toggleSavedJob}
            />
            
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><GraduationCap className="text-blue-600" size={20} /> Skills Curriculum</h2>
            <div className="grid grid-cols-1 gap-4">
                <StepCard 
                    icon={Rocket} 
                    title="0. The Career Multiverse" 
                    description="Discover your true workplace personality type." 
                    status={'active'} 
                    onClick={() => onNavigate('archetype-engine')} 
                />

                <StepCard 
                    icon={Cpu} 
                    title="1. AI Resume Grader" 
                    description="Beat the bots. Get your ATS score instantly." 
                    status={step1Completed ? 'completed' : 'active'} 
                    onClick={() => onNavigate('resume-grader')} 
                    onReview={() => onReview('resume-grader')} 
                    onStartFresh={() => onStartFresh('resume-grader')} 
                />
                
                <StepCard 
                    icon={BookOpen} 
                    title="2. Skills Curriculum" 
                    description="Master the 13 essential modules for workforce readiness." 
                    status={step2Completed ? 'completed' : 'active'} 
                    onClick={() => onNavigate('curriculum-hub')} 
                    onReview={() => onReview('curriculum-hub')} 
                />
                
                {/* THRESHOLD LOCK: Requires completion of Part 1 (or defined threshold) to unlock */}
                <StepCard 
                    icon={PiggyBank} 
                    title="3. Macroeconomic Strain Simulator" 
                    description="Navigate localized wage deflation and logistical constraints." 
                    status={step3Completed ? 'completed' : (budgetUnlocked ? 'active' : 'locked')} 
                    onClick={() => onNavigate('budget-sim')} 
                    onReview={() => onReview('budget-sim')} 
                    onStartFresh={() => onStartFresh('budget-sim')} 
                />
            </div>
        </div>
      </div>
    </div>
  );
}